import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { BsCalendar, BsSearch, BsChevronDown, BsChevronUp } from 'react-icons/bs';
import AOS from 'aos';

interface NewsItem {
  id?: number;
  _id?: string;
  type: 'Événement' | 'Actualité' | 'news' | 'event';
  date?: string;
  createdAt?: string;
  eventDate?: string;
  title: string;
  description?: string;
  content?: string;
  image?: string;
  isActive?: boolean;
  fullContentLoaded?: boolean;
}

// Static news data as fallback
const staticNewsItems: NewsItem[] = [
  {
    id: 1,
    type: 'Événement',
    date: '15 Juin 2025',
    title: 'Journée Portes Ouvertes',
    description: 'Découvrez nos locaux, rencontrez nos enseignants et nos étudiants lors de notre prochaine journée portes ouvertes.',
    image: '/images/1.jpg'
  },
  {
    id: 2,
    type: 'Actualité',
    date: '5 Juin 2025',
    title: 'Nouveau Partenariat International',
    description: 'Horizons School signe un nouveau partenariat avec une prestigieuse université européenne pour des échanges étudiants.',
    image: '/images/2.jpg'
  },
  {
    id: 4,
    type: 'Événement',
    date: '10 Juillet 2025',
    title: 'Cérémonie de Remise des Diplômes',
    description: 'Rejoignez-nous pour célébrer la réussite de nos diplômés de la promotion 2025.',
    image: '/images/students.jpg'
  },
  {
    id: 5,
    type: 'Actualité',
    date: '1 Juin 2025',
    title: 'Nouveau Programme de Double Diplôme',
    description: 'Horizons School lance un nouveau programme de double diplôme en partenariat avec une université canadienne.',
    image: '/images/school-building.jpg'
  }
];

const News: React.FC = () => {
  const [newsItems] = useState<NewsItem[]>(staticNewsItems);
  const [filteredItems, setFilteredItems] = useState<NewsItem[]>(staticNewsItems);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('Tous');
  const [expandedItems, setExpandedItems] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
    });
  }, []);

  // Filter news items based on search term and active filter
  useEffect(() => {
    let results = newsItems;
    
    // Apply search filter
    if (searchTerm) {
      results = results.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply type filter
    if (activeFilter !== 'Tous') {
      results = results.filter(item => item.type === activeFilter);
    }
    
    setFilteredItems(results);
  }, [searchTerm, activeFilter, newsItems]);

  const getBadgeVariant = (type: string) => {
    switch (type) {
      case 'Événement':
        return 'primary';
      case 'Actualité':
        return 'success';
      default:
        return 'secondary';
    }
  };
  
  // Toggle expanded state for a news item
  const toggleExpanded = (item: NewsItem) => {
    const id = item._id || item.id;
    if (!id) return;
    
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Always show button for expanding/collapsing content
  const shouldShowButton = (item: NewsItem) => {
    return item.description && item.description.length > 120;
  };

  return (
    <>
      {/* Hero Section with Background Image */}
      <section 
        className="position-relative text-white py-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(13, 110, 253, 0.85), rgba(0, 53, 102, 0.9)), url("/images/campus.jpeg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '300px'
        }}
      >
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={8} data-aos="fade-right">
              <h1 className="display-4 fw-bold mb-4">Actualités & Événements</h1>
              <p className="lead mb-0">
                Restez informé des dernières nouvelles, événements et activités d'Horizons School
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Search and Filter Section */}
      <section className="py-4 bg-light border-bottom">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-3 mb-lg-0">
              <InputGroup>
                <InputGroup.Text className="bg-white border-end-0">
                  <BsSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Rechercher des actualités..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
            </Col>
            <Col lg={6}>
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                {['Tous', 'Événement', 'Actualité'].map(filter => (
                  <Button
                    key={filter}
                    variant={activeFilter === filter ? 'primary' : 'outline-primary'}
                    size="sm"
                    className="rounded-pill px-3"
                    onClick={() => setActiveFilter(filter)}
                  >
                    {filter}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* News Grid */}
      <section className="py-5">
        <Container>
          {filteredItems.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-4">
                <img 
                  src="/images/placeholder.svg" 
                  alt="No results" 
                  style={{ maxWidth: '120px', opacity: 0.5 }}
                />
              </div>
              <h3>Aucun résultat trouvé</h3>
              <p className="text-muted">Essayez de modifier vos critères de recherche</p>
              <Button 
                variant="outline-primary" 
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('Tous');
                }}
              >
                Réinitialiser les filtres
              </Button>
            </div>
          ) : (
            <Row>
              {filteredItems.map((item, index) => {
                const itemId = item._id || item.id;
                const isExpanded = expandedItems.has(itemId as string | number);
                    
                    return (
                      <Col md={6} lg={4} className="mb-4" key={itemId}>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card className="h-100 border-0 shadow-sm hover-lift overflow-hidden">
                            <div className="card-img-top position-relative overflow-hidden">
                              <img 
                                src={item.image}
                                alt={item.title}
                                className="img-fluid"
                                style={{
                                  height: '220px', 
                                  width: '100%', 
                                  objectFit: 'cover',
                                  transition: 'transform 0.5s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                                onError={(e) => {
                                  e.currentTarget.src = '/images/placeholder.svg';
                                }}
                              />
                              <Badge 
                                bg={getBadgeVariant(item.type)} 
                                className="position-absolute top-0 end-0 m-3 px-3 py-2"
                              >
                                {item.type}
                              </Badge>
                            </div>
                            <Card.Body className="p-4">
                              <div className="d-flex align-items-center mb-3">
                                <BsCalendar className="text-primary me-2" />
                                <small className="text-muted">{item.date}</small>
                              </div>
                              <Card.Title className="fw-bold h5 mb-3">{item.title}</Card.Title>
                              <Card.Text className="text-muted">
                                {item.description && isExpanded 
                                  ? item.description
                                  : item.description && item.description.length > 120 
                                    ? `${item.description.substring(0, 120)}...` 
                                    : item.description}
                              </Card.Text>
                              {shouldShowButton(item) && (
                                <Button 
                                  variant="link" 
                                  className="text-primary p-0 d-flex align-items-center mt-2"
                                  onClick={() => toggleExpanded(item)}
                                >
                                  {isExpanded ? (
                                    <>Voir moins <BsChevronUp className="ms-1" /></>
                                  ) : (
                                    <>Voir plus <BsChevronDown className="ms-1" /></>
                                  )}
                                </Button>
                              )}
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Container>
          </section>
    </>
  );
};

export default News; 